<div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 animate-fade-in-up">
  <div class="w-full flex justify-between mb-4">
    <app-btn-back routeTo="/profile/details" icon="fa fa-arrow-left" btnText="Back to Profile"></app-btn-back>
    <div class="flex flex-row gap-4">
      <app-btn-action label="Refresh" (clicked)="refresh()" bgColor="bg-primary-500" textColor="text-white"
        icon="fa fa-refresh"></app-btn-action>
    </div>
  </div>

  <div class="flex flex-col w-full gap-6">

    <!-- Summary Stats Header -->
    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div class="bg-white rounded-lg p-6 shadow border border-blueGray-100 flex items-center justify-between">
        <div>
          <p class="text-xs uppercase font-bold text-blueGray-400">Total Given (Last 3 Months)</p>
          <h3 class="text-2xl font-extrabold text-primary-500 mt-1">
            {{ totalGivenInWindow | currency: primaryCurrency }}
          </h3>
        </div>
        <div class="w-12 h-12 rounded-full bg-primary-50 text-primary-500 flex items-center justify-center text-xl">
          <i class="fas fa-hand-holding-dollar"></i>
        </div>
      </div>

      <div class="bg-white rounded-lg p-6 shadow border border-blueGray-100 flex items-center justify-between">
        <div>
          <p class="text-xs uppercase font-bold text-blueGray-400">Records Shown</p>
          <h3 class="text-2xl font-extrabold text-blueGray-700 mt-1">
            {{ visiblePayments.length }} / {{ isShowingOlderBeyond3Months ? allUserPayments.length : threeMonthPayments.length }}
          </h3>
        </div>
        <div class="w-12 h-12 rounded-full bg-blue-50 text-secondary-600 flex items-center justify-center text-xl">
          <i class="fas fa-receipt"></i>
        </div>
      </div>
    </div>

    <!-- Giving History Card -->
    <div class="w-full min-w-56 bg-white rounded-lg shadow border border-blueGray-100 overflow-hidden">
      <div class="p-4 border-b border-blueGray-200 flex flex-wrap justify-between items-center gap-2">
        <div class="flex items-center gap-2">
          <h6 class="text-primary-500 text-lg font-bold uppercase">
            My Giving History
          </h6>
        </div>
        <div class="flex items-center gap-2">
          <span class="text-xs bg-blue-50 text-secondary-600 px-3 py-1 rounded-full font-bold uppercase">
            {{ isShowingOlderBeyond3Months ? 'All Time Records' : 'Last 3 Months' }}
          </span>
        </div>
      </div>

      @if (paymentsLoading) {
        <div class="flex flex-col items-center justify-center py-12">
          <i class="fa fa-spinner fa-spin text-3xl text-primary-500 mb-3"></i>
          <span class="text-sm font-medium text-blueGray-500">Loading your giving history...</span>
        </div>
      } @else if (visiblePayments.length === 0) {
        <div class="text-center py-12">
          <i class="fa-solid fa-receipt text-5xl text-blueGray-300 mb-4"></i>
          <p class="text-base font-semibold text-blueGray-600">No giving records found in the last 3 months.</p>
          <p class="text-xs text-blueGray-400 mt-1">Your tithes and contributions will appear here once recorded.</p>
        </div>
      } @else {
        <div class="block w-full overflow-x-auto">
          <table class="items-center w-full bg-transparent border-collapse">
            <thead>
              <tr>
                <th class="px-6 align-middle border-b border-blueGray-200 py-3 text-xs uppercase whitespace-nowrap font-bold text-left bg-blueGray-50 text-blueGray-500">
                  Group / Date
                </th>
                <th class="px-6 align-middle border-b border-blueGray-200 py-3 text-xs uppercase whitespace-nowrap font-bold text-left bg-blueGray-50 text-blueGray-500">
                  Details
                </th>
                <th class="px-6 align-middle border-b border-blueGray-200 py-3 text-xs uppercase whitespace-nowrap font-bold text-left bg-blueGray-50 text-blueGray-500">
                  Amount
                </th>
                <th class="px-6 align-middle border-b border-blueGray-200 py-3 text-xs uppercase whitespace-nowrap font-bold text-left bg-blueGray-50 text-blueGray-500">
                  Status
                </th>
              </tr>
            </thead>
            <tbody>
              @for (payment of visiblePayments; track payment.id) {
                <tr class="hover:bg-blueGray-50 border-b border-blueGray-200 last:border-0 transition-colors">
                  <td class="border-t-0 px-6 align-middle border-l-0 border-r-0 text-xs p-4 text-left">
                    <div class="flex flex-col">
                      <span class="text-xs font-light text-blueGray-400">
                        @if (getPaymentDate(payment)) {
                          {{ getPaymentDate(payment) | date: 'dd MMM yyyy, hh:mm a' }}
                        } @else {
                          N/A
                        }
                      </span>
                      <span class="font-medium text-blueGray-700 text-sm mt-0.5">
                        {{ groupNames[payment.groupId] || 'Loading...' }}
                      </span>
                    </div>
                  </td>
                  <td class="border-t-0 px-6 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4 text-blueGray-600">
                    <div class="flex items-center gap-1.5">
                      <span class="capitalize font-medium">{{ payment.type === 'tithe' ? 'Tithe' : 'Donation' }}</span>
                      @if (payment.fundingId) {
                        <span class="text-[10px] bg-blue-50 text-secondary-600 px-1.5 py-0.5 rounded font-semibold">Fundraiser</span>
                      }
                      @if (payment.stripeSubscriptionId) {
                        <span class="text-[10px] bg-indigo-50 text-indigo-600 px-1.5 py-0.5 rounded font-semibold flex items-center gap-0.5"><i class="fa fa-sync text-[8px]"></i> Recurring</span>
                      }
                    </div>
                  </td>
                  <td class="border-t-0 px-6 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4 font-bold text-primary-500">
                    {{ payment.amount | currency: (payment.currency || 'USD') }}
                  </td>
                  <td class="border-t-0 px-6 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4">
                    <span class="px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider"
                      [ngClass]="payment.status === 'succeeded' || !payment.status ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'">
                      {{ payment.status || 'succeeded' }}
                    </span>
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>

        @if (hasMoreItems()) {
          <div class="flex justify-center p-4 border-t border-blueGray-200">
            <button (click)="showMore()" type="button"
              class="px-6 py-2.5 bg-white border border-blueGray-300 hover:bg-blueGray-100 text-blueGray-700 text-xs font-bold uppercase rounded-lg shadow-sm hover:shadow transition-all flex items-center gap-2 cursor-pointer">
              <i class="fas fa-chevron-down"></i>
              Show More (+10)
            </button>
          </div>
        }
      }
    </div>

  </div>
</div>
